// InputField component which group label and control 
// Version 1.6

import { getTranslated } from "../../utils/CommonUtils"
import { useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { Input } from "antd"
import { validateRegex } from "../../utils/CommonUtils"
import { EyeFilled, EyeInvisibleFilled } from "@ant-design/icons"
import { PURPLE } from "../../styles/styleUtils"

const InputField = (props) => {
    const [error, setError] = useState("");
    const [value, setValue] = useState("");
    const [regex, setRegex] = useState();
    const [max, setMax] = useState(255);
    const [characterRegex, setCharacterRegex] = useState();

    const dict = useSelector((state) => state.common.dict);

    // Component reset implementation
    useEffect(() => {
        setValue("");
        setError("");
    }, [props.reset]);

    useEffect(() => {
        if (!props.value && props.defaultValue !== "") {
            setValue(props.defaultValue)
        }
        else {
            if (!props.value) {
                setValue("");
            }
            else {
                setValue(props.value)
            }
        }

        manageError("");

        // Modify the regular expression from the profile
        if (props.regexp) {
            setRegex("^" + props.regexp + "$");
            setMax(maxChars()); // Set the maximum allowed characters
            setCharacterRegex("^" + validateChars() + "$"); // Set the regex to validate only the chars
            
            // Set error if the value doesn't match the regexp
            if ((props.value !== undefined) && (!validateRegex(props.regexp, props.value))) {
                manageError("error"); 
            } else {
                // Set error if the defaultValue doesn't match the regexp
                if ((props.value === undefined) && (props.defaultValue !== undefined) && (!validateRegex(props.regexp, props.defaultValue))) {
                    manageError("error"); 
                }
            }
        }  

    }, [props.defaultValue, props.regexp])

    // Two level check based on regex
    const handleChange = (event) => {
        const val = event?.target?.value;
        if (regex) {
            if (validateRegex(regex, val)) {
                manageError("");
                setValue(val);
                props.handleChange && props.handleChange(val);
            } else {
                // Two error cases can occur:
                // 1. wrong character
                // 2. length error

                // Special case if the limit number is reached <- implemented in maxlength from Input control
                // if (val.length >= max) {
                //     manageError("");   // No error, just do nothing
                // }
                // else {
                if (validateRegex(characterRegex, val)) {  // Validate against characters without size constraint
                    // ok, so it it size error
                    setValue(val);
                    manageError("error");
                    props.handleChange && props.handleChange(val);  // This has to be validated !!!!!
                }
                else {
                    // not ok, so it is character error
                    if (!validateRegex(regex, value)) {
                        manageError("error");
                    }
                    // If no character in the control, set value to empty to redraw without the wrong character
                    if (value === undefined)
                        setValue("");
                }
                // }
            }
        } else {
            setValue(val);
            props.handleChange && props.handleChange(val);
        }
    }

    const handleBlur = (value) => {
        if (props.handleBlur)
            props.handleBlur(value);
    }

    // Error handler
    const manageError = (value) => {
        if (props.handleError) {
            if (error !== value) {
                props.handleError(value === "error" ? true : false);
            }
        }
        setError(value);
    }

    // Calculate the max allowed characters
    const maxChars = () => {
        let maxAllowed = 255;

        if ((props.regexp) && (props.validationPattern === undefined)) {
            // Find occurence of '{'
            let count = props.regexp.indexOf("{");
            if (count) {
                let countStr = props.regexp.substring(count+1, props.regexp.length-1);
                count = countStr.indexOf(",");
                if (count > 0) {
                    maxAllowed = countStr.substring(count+1);
                }
                else {
                    maxAllowed = countStr;
                }
            }
        }
        return maxAllowed;
    }

    // Identify the regex for character testing
    const validateChars = () => {
        let result = "";

        if (props.regexp) {
            // Check if validation pattern is given ex {0,2}
            // Important note: to be able to erase back the first value should be 0, 
            // else the minimum characters will remain according to the the first value
            if (props.validationPattern !== undefined && (props.validationPattern.startsWith("{") || 
                props.validationPattern.startsWith("[") || 
                props.validationPattern.startsWith("."))) {
                    result = props.validationPattern;
            }
            else {
                if (props.validationPattern === undefined) {
                    let count = props.regexp.indexOf("{");
                    if (count > 0)
                        result = props.regexp.substring(0, count) + "{0,255}";
                    else
                        result = props.regexp + "{0,255}";
                }
                else {
                    switch (props.validationPattern) {
                        case 'IPv4':
                            result = "[0-9.]{0,20}"
                            break;
                        case 'IPv6':
                            result = "[0-9a-fA-F:]{0,50}"
                            break;
                        case 'MAC':
                            result = "[0-9a-fA-F-:]{0,30}"
                            break;
                        case 'Domain':
                            result = "[0-9a-zA-Z-.]{0,255}"
                            break;
                        case 'CCProductID':
                            result = "[0-9a-f-]{0,25}"
                            break;
                        case 'None':
                            result = ".{0,255}";
                            break;
                        default:
                            if (props.regexp.substring(0,3) === "$|^") {
                                result = props.regexp.substring(3);
                            }
                            else
                                result = props.regexp + "{0,255}";
                            break;
                    }
                }
            }
        }
        return result;
    }

    return (
        <div className="field-wrap">
            <div className="sip-textfield-label">{props.label}</div>
            { (() => {
                if ((props.contentDisplay === true) || (props.contentDisplay === undefined)) {
                    return (
                    <Input
                        className="sip-input-text" 
                        value={value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        status={error}
                        placeholder={(props.placeHolder !== undefined) ? props.placeHolder : error ? getTranslated(dict, "PH_MANDATORY_FIELD") : ""}
                        maxLength={max}
                        onClick={props.onClick}
                        disabled={props.disabled}
                        onPressEnter={error==="error" ? null : props.onPressEnter}
                        />)
                }
                else {
                    return(
                    <Input.Password
                        className="sip-input-text" 
                        value={value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        status={error}
                        placeholder={(props.placeHolder !== undefined) ? props.placeHolder : error ? getTranslated(dict, "PH_MANDATORY_FIELD") : ""}
                        maxLength={max}
                        onClick={props.onClick}
                        disabled={props.disabled}
                        onPressEnter={error==="error" ? null : props.onPressEnter}
                        iconRender={(visible) => (visible ? <EyeFilled style={{ color: PURPLE }} /> : <EyeInvisibleFilled style={{ color: PURPLE }} />)} /> 
                    )
                }
            })()
            }
        </div>
    )
}

export default InputField;